import type { AssetClassConstants } from '@/constants/AssetClassConstants';
import type { Currency } from '@/constants/Currency';
import type { FactorConstants } from '@/constants/FactorConstants';
import type { Region } from '@/constants/Region';
import type { Status } from '@/constants/Status';
import type { SuspendStatus } from '@/constants/SuspendStatus';
import type { StyleConstants } from '@/constants/StyleConstants';
import type { TimeSeriesEntityTypeConstants } from '@/constants/TimeSeriesEntityTypeConstants';
import type { TrackTypeConstants } from '@/constants/TrackTypeConstants';
import type { CurrencyConversion } from './CurrencyConversion';
import type { AllocationDTO } from './portfolio/AllocationDTO';
import type { PortfolioType } from './portfolio';
import type { IBenchmark, IPrivateFundBenchmarks } from './strategy';
import type { StrategyItemResponseDTO, PortfolioItemResponseDTO } from '@/api-v2/web/discover';
import type { StrategyItemSubset } from '@/types/StrategyItemSubset';
import type { Locales } from '@/constants/Locales';
import type { ReturnTypeConstants } from '@/constants/ReturnTypeConstants';
import type { MeasurementTypeConstants } from '@/constants/MeasurementTypeConstants';
import type { BasketCommentTypeConstants } from '@/constants/BasketCommentTypeConstants';
import type { BasketStatusConstants } from '@/constants/BasketStatusConstants';
import { PortfolioVersionType } from '@/constants/PortfolioVersionType';
import type { BasketCommentDTO } from '@/api-v2/admin/basket/types/BasketCommentDTO';
import type { PortfolioTreeGroupingDTO } from './portfolio/PortfolioTreeGroupingDTO';
import type { PortfolioViewMode } from '@/constants/PortfolioViewMode';
import type { ReturnCategoryConstants } from '@/constants/ReturnCategoryConstants';
import type { StrategyFactsheetResponseDTO } from '@/api-v2/web/strategies';
import type { TimeSeriesEntityStatus } from '@/types/admin/strategies/StrategyStatus';
import type { AvailableDateRange } from '@/types/AvailableDateRange';

export interface IPortfolioTree {
  allocation: {
    [portfolioTreeId: string]: AllocationDTO;
  };
  draftOf: number | null;
  isBenchmark: boolean;
  mask?: {
    [strategyCode: string]: {
      longName: {
        [locale in Locales]: string;
      };
      shortName: {
        [locale in Locales]: string;
      };
      tickerShortName: {
        [locale in Locales]: string;
      };
      code?: string | null;
      provider?: string | null;
    };
  };
  oldPortfolioId: number;
  originalSlug: string;
  portfolioId: string;
  portfolioListId: number | null;
  portfolioTree: IPortfolioTreeSubportfolio;
  parameters: PortfolioTreeParametersDTO | null;
  apiConfig: PortfolioTreeApiConfig | null;
  slug: string;
  slugOrCode: string | null;
  teamId: number;

  /**
   * Datepicker availability from the API. Optional on older APIs.
   * When present, use `startDate` as the picker min (trust the API; do not re-derive from tree dates).
   */
  availableDateRange?: AvailableDateRange;

  createdAt: string;
  createdBy: number;
  deletedAt: string;
  updatedAt: string;
  updatedBy: number;
}

// FIXME: Rename and remove the `I` prefix
export enum IPortfolioTreeChildType {
  PORTFOLIO = 'portfolio',
  CONSTITUENT = 'constituent',
  TIMESERIES = 'timeseries',
}

export interface IAppendPortfolioTreeParams {
  portfolioTree: IPortfolioTreeSubportfolio | IPortfolioTreeStrategy;
  allocation: {
    [portfolioTreeId: string]: AllocationDTO;
  };
}

export interface ISetPortfolioTreeParams {
  portfolioTree: IPortfolioTreeSubportfolio;
  allocation: {
    [portfolioTreeId: string]: AllocationDTO;
  };
  isBenchmark: boolean;
  /**
   * Used for the restore to live snapshot
   */
  indexUniqueIdentifier?: string;

  /**
   * Used for smart portfolio creation
   */
  userPrompt?: string;
}

type NonLiveVersionTypeParams = {
  /**
   * used for Portfolio versioning
   */
  version?: PortfolioVersionType.SIMULATION | PortfolioVersionType.PROVISIONAL;
};

type PositionVersionTypeParams = {
  /**
   * Used on Constituent portfolios
   *
   * The API uses this date in two ways:
   * 1. It selects the appropriate Position Date that is equal to or before this date
   * 2. It calculates the risk metrics as of this date
   */
  analysisDate?: string;
  /**
   * used for Portfolio versioning
   */
  version?: PortfolioVersionType.POSITION | PortfolioVersionType.SIMULATION | PortfolioVersionType.PROVISIONAL;
};

type HistoryVersionTypeParams = {
  /**
   * Used on Constituent portfolios
   *
   * The API uses this date range to create the historical portfolio from the position files between these two dates
   */
  historyDateRange?: [string, string];
  /**
   * used for Portfolio versioning
   */
  version: PortfolioVersionType.HISTORY;
};

export type IPortfolioTreeDraftParams = {
  /**
   * used for Portfolio Default/View mode
   */
  mode: PortfolioViewMode;
} & (NonLiveVersionTypeParams | PositionVersionTypeParams | HistoryVersionTypeParams);

export interface IPortfolioTreeDraftRequestBody {
  groupingConfig?: Partial<Record<PortfolioViewMode, PortfolioTreeGroupingDTO>>;
}

export const isPositionVersionTypeParams = (
  datum: IPortfolioTreeDraftParams,
): datum is IPortfolioTreeDraftParams & PositionVersionTypeParams => {
  return datum.version === PortfolioVersionType.POSITION;
};

export const isHistoryVersionTypeParams = (
  datum: IPortfolioTreeDraftParams,
): datum is IPortfolioTreeDraftParams & HistoryVersionTypeParams => {
  return datum.version === PortfolioVersionType.HISTORY;
};

/**
 *  NOTE: this function will now return false if `datum` is `null`.
 */
export const isPortfolioTreeSubportfolio = (
  datum:
    | IPortfolioTree
    | IPortfolioTreeSubportfolio
    | IPortfolioTreeStrategy
    | PortfolioItemResponseDTO
    | StrategyItemResponseDTO
    | StrategyItemSubset
    | StrategyFactsheetResponseDTO
    | null,
): datum is IPortfolioTreeSubportfolio => {
  return datum !== null && 'type' in datum && 'name' in datum && 'portfolioTreeId' in datum;
};

/**
 *  NOTE: this function will now return false if `datum` is `null`.
 */
export const isPortfolioTreeStrategy = (
  datum:
    | IPortfolioTree
    | IPortfolioTreeSubportfolio
    | IPortfolioTreeStrategy
    | PortfolioItemResponseDTO
    | StrategyItemResponseDTO
    | StrategyItemSubset
    | StrategyFactsheetResponseDTO
    | null,
): datum is IPortfolioTreeStrategy => {
  return datum !== null && 'type' in datum && !('name' in datum) && 'portfolioTreeId' in datum;
};

export const isPortfolioTree = (item: PortfolioItemResponseDTO | IPortfolioTree): item is IPortfolioTree => {
  return (item as IPortfolioTree).originalSlug !== undefined;
};

/**
 * When adding a new required property, add it to the addSubportfolio() in NavigationPanel.vue
 */
export type IPortfolioTreeSubportfolio = {
  portfolioTreeId: string;
  /**
   * parentPortfolioTreeId will only be returned from the api side if this is a draft portfolio
   * This is the original portfolio id of the portfolio tree
   */
  parentPortfolioTreeId?: string;
  type: PortfolioType;
  /**
   * suspended is the user marked value whether to suspend something or not
   */
  suspended: SuspendStatus;
  /**
   * status is set by the database
   */
  status: Status;
  name: string;
  assetClass?: AssetClassConstants;
  region?: Region;
  factor?: FactorConstants;
  /**
   * The index code of a strategy that serves as the benchmark of a given subportfolio
   */
  benchmark?: string;
  /**
   * The portfolioTreeId of the strategy that serves as the base fund of a peer group
   */
  baseFund?: string;
  /**
   * Official reference of a portfolio. Only used to link a Master Index or Custom Basket. User should not edit this field, unless on basket creation
   */
  reference?: string;
  /**
   * User defined reference of a portfolio. Used as the "Portfolio Index" for linked portfolios, custom weighting portfolios or normal portfolios. User may edit this field
   */
  userReference?: string;
  /**
   * Server-owned attribute id linking baskets to saved basket-optimizer parameter sets (optional until first save).
   */
  optimizerParameterId?: string | null;

  apiConfig: PortfolioTreeApiConfig | null;

  style?: StyleConstants;
  customCategory?: string[];
  description?: string;
  components: (IPortfolioTreeStrategy | IPortfolioTreeSubportfolio)[];
  activeCodes?: string[];

  /**
   * Differs from `flattenedAllCodesForRisk` in that *this* array may NOT contain
   * portfolio tree items that are missing strategies or references.
   *
   * Additionally, per the name this array will filter out items that are not Status.ACTIVE
   */
  flattenedActiveCodes?: string[];

  flattenedAllCodes?: string[];

  /**
   * Differs from `flattenedActiveCodes` in that *this* array MAY contain
   * portfolio tree items that are missing strategies or references (important for direct holdings, etc)
   *
   * This array will not look at the `status` property.
   */
  flattenedAllCodesForRisk?: string[];
  isInherited: boolean;
  /**
   * historyStartDate: the start of the track. No analysis date may be before this date.
   */
  historyStartDate?: string | null;
  /**
   * effectiveHistoryStartDate: the start of the track. No analysis date may be before this date. Adjusted for fx conversion
   */
  effectiveHistoryStartDate?: string | null;
  inceptionDate?: string | null;
  /**
   * customInceptionDate: the user defined date to overwrite the calculated inception date (Root portfolio only)
   */
  customInceptionDate?: string;
  /**
   * customStartDate: the user defined date of the start of the track. Able to be the earliest history start date across all portfolio components (Root portfolio only)
   */
  customStartDate?: string;
  weighting?: number;
  unit?: number;
  initialWeighting?: number;
  initialUnit?: number;
  positionDate?: string;
  backtestDates?: string[];
  pointerSlug?: string;
  portfolioValue?: number;
  /**
   * When the `groupingConfig` has a `PortfolioViewMode.VIEW` property, then when the draft is calculated, the API will:
   * 1. calculate this draft (the DEFAULT mode draft) as usual and make no changes to it otherwise, and
   * 2. create a new draft (the VIEW mode draft) by flattening and then re-grouping the items of the
   *    tree according to the settings in the `groupingConfig`. This VIEW draft exists only temporarily
   *    in the database and will be not be persisted across page refreshes.
   *
   * When the `groupingConfig` has a `PortfolioViewMode.DEFAULT` property, then when the draft
   * is calculated, it will apply a complete transformation of the portfolio tree, such that the
   * tree is flattened and then re-grouped according to the settings in the `groupingConfig`. If
   * the user chooses to save the portfolio tree afterwards, then it will completely replace the
   * original tree with the transformed tree.
   */
  groupingConfig?: Record<PortfolioViewMode, PortfolioTreeGroupingDTO>;
  version?: PortfolioVersionType;

  /**
   * Only available on the master level
   *
   * When submitting a calculation mutation, this must always be in DEFAULT mode
   */
  mode?: PortfolioViewMode | null;
} & CurrencyConversion &
  IBasketPortfolioInfo;

/**
 * When adding a new required property, add it to the onStrategiesAdded() in NavigationPanel.vue
 */
export type IPortfolioTreeStrategy = {
  portfolioTreeId: string;
  initialWeighting?: number;
  initialUnit?: number;
  crossReferenceId?: string;
  reference?: string;
  referenceType?: string;
  isInherited: boolean;
  /**
   * User saved input for suspended
   */
  suspended: SuspendStatus;
  /**
   * `status` overrides `suspended`. Or the track may be invalid, which trumps `suspended`
   */
  status: Status;
  type: IPortfolioTreeChildType;
  timeseriesId?: string;
  /**
   * Present when this leaf is an instrument holding.
   */
  securityId?: number | null;
  weighting?: number;
  unit?: number;
  rebalancingCost?: number;
  replicationCost?: number;
  backtestRebalancingCosts?: number[];
  backtestReplicationCosts?: number[];
  /**
   * basketComplianceStatus remains unused until we have a more mature compliance engine
   */
  basketComplianceStatus?: BasketCommentTypeConstants;
  strategy?: IPortfolioTreeStrategyStaticInfo;
  /**
   * Data accessibility of the strategy. Does the user have the correct access right to the track level, position level, and risk level
   */
  dataAccessibility?: IPortfolioTreeStrategyDataStatus;
  /**
   * Data availability of the strategy. Is there data available at the track level, position level, and risk level
   */
  dataAvailability?: IPortfolioTreeStrategyDataStatus;

  /**
   * Master index codes that this strategy is under (used for qis option inheritance)
   */
  parentMasterIndexCodes?: string[];
} & CurrencyConversion &
  IBasketStrategyInfo;

export type IPortfolioTreeStrategyStaticInfo = {
  id: string;
  code: string;
  trackType: TrackTypeConstants;
  type: TimeSeriesEntityTypeConstants;
  shortName: string;
  longName: string;
  assetClass: AssetClassConstants;
  factor: FactorConstants;
  style: StyleConstants | null;
  region: Region;
  provider: string;
  returnType: ReturnTypeConstants | null;
  currency: Currency;
  internalReference: string | null;
  isin: string[];
  morningStarId: string[];
  ric: string[];
  ticker: string[];
  sedol: string[];
  historyStartDate: string | null;
  effectiveHistoryStartDate: string | null;
  inceptionDate: string | null;
  benchmarks?: IBenchmark | IPrivateFundBenchmarks | null; // Warning: Not Implemented on API (as of 2022-10-08)
  country?: string | null;
  sector?: string | null;
  status?: TimeSeriesEntityStatus | null;
  retirementDate?: string | null;
};

type IPortfolioTreeStrategyDataStatus = {
  // Is there data available or accessible at the track level
  track: boolean;
  // Is there data available or accessible at the position level
  position: boolean;
  // Is there data available or accessible at the risk level
  risk: boolean;
  // Is there static data available
  static?: boolean;
};

type IBasketStrategyInfo = {
  basketComments?: BasketCommentDTO[];
};

type IBasketPortfolioInfo = {
  basketStatus?: BasketStatusConstants;
  basketSubmissionDate?: string;
  returnType?: ReturnTypeConstants;
  returnCategory?: ReturnCategoryConstants;
  originalMeasurementType?: MeasurementTypeConstants;
  basketComments?: BasketCommentDTO[];
  basketVersion?: number;
  basketImplementationDate?: string;
  basketObservationDate?: string;
  basketBusinessEvent?: string;
  basketDivisor?: number;
  basketClaimedBy?: string;
  /**
   * Saves the initial price of the basket when user first submits the basket (i.e. First version of the basket)
   */
  initialPrice?: number;
};

export type ComponentWithPriceBase = {
  convertedStrategyPrice?: number;
  parentPortfolioNav: number;
  calculatedItemNotional: number;
  basketPrice?: number;
  unleveragedNotional?: number;
};

export type IPortfolioTreeStrategyWithPrice = IPortfolioTreeStrategy & ComponentWithPriceBase;
export type IPortfolioTreeSubportfolioWithPrice = IPortfolioTreeSubportfolio & ComponentWithPriceBase;
export type IPortfolioTreeWithPrice = IPortfolioTree & {
  portfolioTree: IPortfolioTreeSubportfolioWithPrice;
};

export interface PortfolioTreeApiConfig {
  shouldExposePositions: boolean;
  containsOnlyEquities: boolean;
  hasLeverage?: boolean;
}

/**
 * Parameters stored in the portfolios table
 * Determines certain behaviors of the portfolio tree
 */
export interface PortfolioTreeParametersDTO {
  /**
   * Indicates if the portfolio is a read-only portfolio
   */
  isReadOnlyPortfolio?: boolean;

  /**
   * Indicates if the basket is eligible for rebalancing
   */
  isRebalanceEligible?: boolean;

  /**
   * Indicates if the portfolio is a custom rebalancing portfolio
   */
  hasCustomRebalancing?: boolean;
  /**
   * The date used by the /draft API to get the price of each item in a Live portfolio and properly calculate weights / units
   *
   * If no analysis date is provided, the API will use the latest available date as the analysis date.
   *
   * For baskets, the analysis date will correspond to the *position* date.
   *
   * For non-baskets, the analysis date will correspond to the *risk* date.
   */
  analysisDate?: string;
}
